/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { supabase } from './supabase'

const CATEGORIES = ['Chest', 'Back', 'Legs', 'Shoulders', 'Arms', 'Core', 'Cardio']
const CARDIO_CATEGORY = 'Cardio'

const CARDIO_MET = {
  'Running': 9.8, 'Cycling': 7.5, 'Rowing': 8.5, 'Jump Rope': 11.0,
  'Elliptical': 5.0, 'Walking': 3.5, 'Stair Climber': 9.0, 'Swimming': 8.0
}

function estimateCalories(sets, weightKg = 84) {
  let total = 0
  const grouped = sets.reduce((acc, s) => {
    if (!acc[s.exercise_name]) acc[s.exercise_name] = []
    acc[s.exercise_name].push(s)
    return acc
  }, {})
  Object.entries(grouped).forEach(([name, exSets]) => {
    if (exSets[0]?.isCardio) {
      const minutes = parseFloat(exSets[0].duration) || 0
      const met = CARDIO_MET[name] || 7.0
      total += Math.round(met * weightKg * (minutes / 60))
    } else {
      total += exSets.length * 5
    }
  })
  return total
}

function calcStreak(history) {
  if (!history.length) return 0
  const dates = [...new Set(history.map(h => h.date))].sort((a, b) => b.localeCompare(a))
  const today = new Date().toISOString().split('T')[0]
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]
  if (dates[0] !== today && dates[0] !== yesterday) return 0
  let streak = 1
  for (let i = 1; i < dates.length; i++) {
    const diff = (new Date(dates[i - 1]) - new Date(dates[i])) / 86400000
    if (diff === 1) streak++
    else break
  }
  return streak
}

function calcWeeklySummary(history, weightKg = 84) {
  const cutoff = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]
  const recent = history.filter(h => h.date >= cutoff)
  const workouts = recent.length
  const totalSets = recent.reduce((sum, h) => sum + (h.workout_sets?.length || 0), 0)
  const days = [...new Set(recent.map(h => h.date))].length
  const totalCalories = recent.reduce((sum, h) => {
    const sets = (h.workout_sets || []).map(s => ({
      exercise_name: s.exercise_name || '',
      isCardio: s.reps > 0 && s.weight === 0,
      duration: s.reps,
    }))
    return sum + estimateCalories(sets, weightKg)
  }, 0)
  return { workouts, totalSets, days, totalCalories }
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

function Fitness() {
  const isMobile = useIsMobile()
  const [exercises, setExercises] = useState([])
  const [activeTab, setActiveTab] = useState('log')
  const [selectedCategory, setSelectedCategory] = useState('Chest')
  const [workoutDate, setWorkoutDate] = useState(new Date().toISOString().split('T')[0])
  const [workoutSets, setWorkoutSets] = useState([])
  const [history, setHistory] = useState([])
  const [selectedLog, setSelectedLog] = useState(null)
  const [selectedLogSets, setSelectedLogSets] = useState([])
  const [editingSet, setEditingSet] = useState(null)
  const [editValues, setEditValues] = useState({})
  const [userEmail, setUserEmail] = useState('local')
  const [showExercisePicker, setShowExercisePicker] = useState(false)
  const [showSetModal, setShowSetModal] = useState(false)
  const [pendingExercise, setPendingExercise] = useState(null)
  const [numSets, setNumSets] = useState(3)
  const [saving, setSaving] = useState(false)
  const [workoutNotes, setWorkoutNotes] = useState('')
  const [personalRecords, setPersonalRecords] = useState([])
  const [newPRs, setNewPRs] = useState([])
  const [prFilter, setPrFilter] = useState('All')
  const [userWeightLbs, setUserWeightLbs] = useState(() => parseFloat(localStorage.getItem('bf_weight_lbs') || '185'))
  const [showWeightModal, setShowWeightModal] = useState(false)
  const [weightInput, setWeightInput] = useState('')

  useEffect(() => {
    fetchExercises()
    getUser()
  }, [])

  useEffect(() => {
    if (userEmail && userEmail !== 'local') {
      fetchHistory()
      fetchPersonalRecords()
    }
  }, [userEmail])

  async function getUser() {
    try {
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith('CF_Authorization='))
      if (cookie) {
        const token = cookie.trim().replace('CF_Authorization=', '')
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        if (payload.email) { setUserEmail(payload.email); return }
      }
    } catch {}
    const cached = localStorage.getItem('bf_user_email')
    setUserEmail(cached || 'local')
  }

  async function fetchExercises() {
    const { data, error } = await supabase.from('exercises').select('*').order('category').order('name')
    if (!error) setExercises(data || [])
  }

  async function fetchHistory() {
    const { data, error } = await supabase
      .from('workout_logs')
      .select('*, workout_sets(*)')
      .eq('user_email', userEmail)
      .order('date', { ascending: false })
      .limit(60)
    if (!error) setHistory(data || [])
  }

  async function fetchPersonalRecords() {
    const { data, error } = await supabase
      .from('personal_records')
      .select('*')
      .eq('user_email', userEmail)
      .order('exercise_name')
    if (!error) setPersonalRecords(data || [])
  }

  async function checkAndUpdatePRs(savedSets) {
    const strengthSets = savedSets.filter(s => !s.isCardio && parseFloat(s.weight) > 0)
    if (!strengthSets.length) return []
    const newPRList = []
    for (const set of strengthSets) {
      const existing = personalRecords.find(pr => pr.exercise_id === set.exercise_id)
      if (!existing || parseFloat(set.weight) > parseFloat(existing.best_weight)) {
        const payload = {
          user_email: userEmail,
          exercise_id: set.exercise_id,
          exercise_name: set.exercise_name,
          best_weight: parseFloat(set.weight),
          best_reps: parseInt(set.reps) || 0,
          achieved_date: workoutDate
        }
        if (existing) {
          await supabase.from('personal_records').update(payload).eq('id', existing.id)
        } else {
          await supabase.from('personal_records').insert([payload])
        }
        newPRList.push(set.exercise_name)
      }
    }
    return newPRList
  }

  async function fetchLogDetail(log) {
    const { data, error } = await supabase
      .from('workout_sets')
      .select('*, exercises(name, category)')
      .eq('workout_log_id', log.id)
      .order('set_number')
    if (!error) {
      setSelectedLogSets(data || [])
      setSelectedLog(log)
      setEditingSet(null)
      setEditValues({})
    }
  }

  function startEditSet(set) {
    setEditingSet(set.id)
    setEditValues({ weight: set.weight, reps: set.reps })
  }

  async function saveEditSet(set) {
    setSaving(true)
    await supabase.from('workout_sets').update({
      weight: parseFloat(editValues.weight) || 0,
      reps: parseInt(editValues.reps) || 0
    }).eq('id', set.id)
    setEditingSet(null)
    setEditValues({})
    setSaving(false)
    fetchLogDetail(selectedLog)
  }

  function cancelEdit() {
    setEditingSet(null)
    setEditValues({})
  }

  function handleExerciseSelect(exercise) {
    setPendingExercise(exercise)
    if (exercise.category === CARDIO_CATEGORY) {
      const cardioEntry = {
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        exercise_category: exercise.category,
        set_number: 1,
        duration: '',
        distance: '',
        isCardio: true
      }
      setWorkoutSets(prev => {
        const filtered = prev.filter(s => s.exercise_name !== exercise.name)
        return [...filtered, cardioEntry]
      })
      setShowExercisePicker(false)
      setPendingExercise(null)
    } else {
      setNumSets(3)
      setShowSetModal(true)
      setShowExercisePicker(false)
    }
  }

  function confirmAddExercise() {
    const newSets = Array.from({ length: numSets }, (_, i) => ({
      exercise_id: pendingExercise.id,
      exercise_name: pendingExercise.name,
      exercise_category: pendingExercise.category,
      set_number: i + 1,
      weight: '',
      reps: '',
      isCardio: false
    }))
    setWorkoutSets(prev => [...prev, ...newSets])
    setShowSetModal(false)
    setPendingExercise(null)
  }

  function updateSet(index, field, value) {
    setWorkoutSets(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s))
  }

  function removeExercise(exerciseName) {
    setWorkoutSets(prev => prev.filter(s => s.exercise_name !== exerciseName))
  }

  async function saveWorkout() {
    if (!workoutSets.length) return
    setSaving(true)
    const { data: log, error: logError } = await supabase
      .from('workout_logs')
      .insert([{ date: workoutDate, user_email: userEmail, notes: workoutNotes }])
      .select()
    if (logError || !log) { setSaving(false); return }

    const sets = workoutSets.map(s => ({
      workout_log_id: log[0].id,
      exercise_id: s.exercise_id,
      set_number: s.set_number,
      weight: s.isCardio ? parseFloat(s.distance) || 0 : parseFloat(s.weight) || 0,
      reps: s.isCardio ? parseInt(s.duration) || 0 : parseInt(s.reps) || 0,
      user_email: userEmail
    }))

    const { error: setsError } = await supabase.from('workout_sets').insert(sets)
    if (!setsError) {
      const prs = await checkAndUpdatePRs(workoutSets)
      setNewPRs(prs)
      fetchPersonalRecords()
      setWorkoutSets([])
      setWorkoutNotes('')
      fetchHistory()
      setActiveTab('history')
    }
    setSaving(false)
  }

  async function deleteWorkout(logId) {
    await supabase.from('workout_sets').delete().eq('workout_log_id', logId)
    await supabase.from('workout_logs').delete().eq('id', logId)
    setSelectedLog(null)
    setSelectedLogSets([])
    fetchHistory()
  }

  const userWeightKg = userWeightLbs / 2.205
  const filteredExercises = exercises.filter(e => e.category === selectedCategory)
  const groupedSets = workoutSets.reduce((acc, set, index) => {
    if (!acc[set.exercise_name]) acc[set.exercise_name] = []
    acc[set.exercise_name].push({ ...set, index })
    return acc
  }, {})
  const totalExercises = Object.keys(groupedSets).length
  const currentCalories = estimateCalories(workoutSets, userWeightKg)
  const streak = calcStreak(history)
  const weekly = calcWeeklySummary(history, userWeightKg)

  const groupedLogSets = selectedLogSets.reduce((acc, set) => {
    const name = set.exercises?.name || 'Unknown'
    if (!acc[name]) acc[name] = { category: set.exercises?.category, sets: [] }
    acc[name].sets.push(set)
    return acc
  }, {})

  const prCategories = ['All', ...new Set(personalRecords.map(pr => {
    const ex = exercises.find(e => e.id === pr.exercise_id)
    return ex?.category || 'Other'
  }))]

  const filteredPRs = prFilter === 'All'
    ? personalRecords
    : personalRecords.filter(pr => {
        const ex = exercises.find(e => e.id === pr.exercise_id)
        return ex?.category === prFilter
      })

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif', maxWidth: '1100px' }}>

      <style>{`input[type="number"],input[type="text"],input[type="date"],select,textarea{font-size:16px!important}`}</style>

      {/* New PR celebration */}
      {newPRs.length > 0 && (
        <div style={{ background: 'linear-gradient(135deg, #ff375f, #ff9f0a)', borderRadius: '16px', padding: '20px', marginBottom: '16px', color: 'white', textAlign: 'center' }}>
          <p style={{ fontSize: '32px', marginBottom: '8px' }}>🏆</p>
          <p style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>New Personal Record{newPRs.length > 1 ? 's' : ''}!</p>
          <p style={{ fontSize: '14px', opacity: 0.9 }}>{newPRs.join(', ')}</p>
          <button onClick={() => setNewPRs([])} style={{ marginTop: '12px', padding: '8px 20px', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '20px', color: 'white', cursor: 'pointer', fontSize: '13px' }}>Dismiss</button>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '-0.5px', marginBottom: '2px' }}>Fitness</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <div style={{ display: 'flex', background: 'var(--bg-tertiary)', borderRadius: '10px', padding: '3px', gap: '2px' }}>
          <button onClick={() => { setActiveTab('log'); setSelectedLog(null) }} style={{ padding: '8px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', background: activeTab === 'log' ? 'white' : 'transparent', color: activeTab === 'log' ? '#1c1c1e' : '#8e8e93', boxShadow: activeTab === 'log' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>Log</button>
          <button onClick={() => { setActiveTab('history'); setSelectedLog(null) }} style={{ padding: '8px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', background: activeTab === 'history' ? 'white' : 'transparent', color: activeTab === 'history' ? '#1c1c1e' : '#8e8e93', boxShadow: activeTab === 'history' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>History</button>
          <button onClick={() => { setActiveTab('summary'); setSelectedLog(null) }} style={{ padding: '8px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', background: activeTab === 'summary' ? 'white' : 'transparent', color: activeTab === 'summary' ? '#1c1c1e' : '#8e8e93', boxShadow: activeTab === 'summary' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>Summary</button>
          <button onClick={() => { setActiveTab('prs'); setSelectedLog(null) }} style={{ padding: '8px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', background: activeTab === 'prs' ? 'white' : 'transparent', color: activeTab === 'prs' ? '#1c1c1e' : '#8e8e93', boxShadow: activeTab === 'prs' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>PRs 🏆</button>
        </div>
      </div>

      {/* STREAK BANNER */}
      {streak > 0 && (
        <div style={{ background: 'linear-gradient(135deg, #ff6b35, #ff9f0a)', borderRadius: '16px', padding: '16px 20px', marginBottom: '16px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '28px' }}>🔥</span>
          <div>
            <p style={{ fontSize: '17px', fontWeight: '700', marginBottom: '2px' }}>{streak}-Day Streak</p>
            <p style={{ fontSize: '13px', opacity: 0.85 }}>Keep it going — log a workout today to extend it</p>
          </div>
        </div>
      )}

      {/* LOG TAB */}
      {activeTab === 'log' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 3px var(--shadow)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--border-light)', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.8px' }}>DATE</p>
              <input type="date" value={workoutDate} onChange={e => setWorkoutDate(e.target.value)} style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-secondary)', background: 'var(--bg-input)' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {totalExercises > 0 && (
                <div style={{ background: 'var(--bg-tertiary)', borderRadius: '10px', padding: '8px 14px', textAlign: 'center' }}>
                  <span style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#ff375f', lineHeight: 1.1 }}>{totalExercises}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>exercises</span>
                </div>
              )}
              {totalExercises > 0 && (
                <div style={{ background: 'var(--bg-tertiary)', borderRadius: '10px', padding: '8px 14px', textAlign: 'center' }}>
                  <span style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#ff9f0a', lineHeight: 1.1 }}>{currentCalories}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>est. kcal</span>
                </div>
              )}
              <button onClick={() => setShowExercisePicker(true)} style={{ padding: '10px 18px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                + Add Exercise
              </button>
            </div>
          </div>

          {Object.keys(groupedSets).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ fontSize: '40px', marginBottom: '12px' }}>🏋️</p>
              <p style={{ fontSize: '15px', color: 'var(--text-tertiary)' }}>Tap + Add Exercise to start your workout</p>
            </div>
          ) : (
            <div>
              {Object.entries(groupedSets).map(([name, sets]) => {
                const isCardio = sets[0].isCardio
                const pr = personalRecords.find(p => p.exercise_name === name)
                return (
                  <div key={name} style={{ marginBottom: '16px', background: 'var(--bg-input)', borderRadius: '12px', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>{name}</p>
                      <button onClick={() => removeExercise(name)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#ff375f' }}>Remove</button>
                    </div>
                    {pr && !isCardio && (
                      <p style={{ fontSize: '11px', color: '#ff9f0a', fontWeight: '600', marginBottom: '10px' }}>🏆 PR: {pr.best_weight} lbs x {pr.best_reps} reps</p>
                    )}
                    {isCardio ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                        <div>
                          <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '6px' }}>DURATION (min)</p>
                          <input placeholder="0" type="number" value={sets[0].duration} onChange={e => updateSet(sets[0].index, 'duration', e.target.value)} style={inputStyle} />
                        </div>
                        <div>
                          <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '6px' }}>DISTANCE (mi)</p>
                          <input placeholder="0.0" type="number" step="0.1" value={sets[0].distance} onChange={e => updateSet(sets[0].index, 'distance', e.target.value)} style={inputStyle} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr', gap: '8px', marginBottom: '6px', marginTop: '8px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)' }}>SET</span>
                          <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)' }}>WEIGHT (lbs)</span>
                          <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)' }}>REPS</span>
                        </div>
                        {sets.map(set => {
                          const isPRBeat = pr && parseFloat(set.weight) > parseFloat(pr.best_weight)
                          return (
                            <div key={set.index} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-tertiary)', textAlign: 'center' }}>{set.set_number}</span>
                              <input placeholder="0" type="number" value={set.weight} onChange={e => updateSet(set.index, 'weight', e.target.value)} style={{ ...inputStyle, borderColor: isPRBeat ? '#ff9f0a' : '#e5e5ea' }} />
                              <input placeholder="0" type="number" value={set.reps} onChange={e => updateSet(set.index, 'reps', e.target.value)} style={inputStyle} />
                            </div>
                          )
                        })}
                      </>
                    )}
                  </div>
                )
              })}
              <div style={{ marginTop: '16px', marginBottom: '4px' }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.8px', marginBottom: '8px' }}>WORKOUT NOTES</p>
                <textarea
                  placeholder="How did it feel? Any PRs, observations..."
                  value={workoutNotes}
                  onChange={e => setWorkoutNotes(e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', fontSize: '14px', color: 'var(--text-primary)', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--bg-input)' }}
                />
              </div>
              <button onClick={saveWorkout} disabled={saving} style={{ width: '100%', padding: '14px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '15px', fontWeight: '600', marginTop: '8px' }}>
                {saving ? 'Saving...' : 'Save Workout'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* HISTORY LIST */}
      {activeTab === 'history' && !selectedLog && (
        <div>
          <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.8px', marginBottom: '12px' }}>RECENT WORKOUTS</p>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ fontSize: '40px', marginBottom: '12px' }}>📋</p>
              <p style={{ fontSize: '15px', color: 'var(--text-tertiary)' }}>No workouts logged yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {history.map(log => (
                <div key={log.id} onClick={() => fetchLogDetail(log)} style={{ background: 'var(--bg-card)', borderRadius: '14px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px var(--shadow)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ff375f', flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '2px' }}>
                        {new Date(log.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                      <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{log.workout_sets ? log.workout_sets.length : 0} exercises logged</p>
                    </div>
                  </div>
                  <span style={{ fontSize: '20px', color: 'var(--text-hint)' }}>›</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HISTORY DETAIL */}
      {activeTab === 'history' && selectedLog && (
        <div>
          <button onClick={() => { setSelectedLog(null); setSelectedLogSets([]) }} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#ff375f', fontWeight: '500', marginBottom: '16px', padding: 0 }}>
            Back to History
          </button>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 3px var(--shadow)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--border-light)' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  {new Date(selectedLog.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h2>
                <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{Object.keys(groupedLogSets).length} exercises</p>
              </div>
              <button onClick={() => deleteWorkout(selectedLog.id)} style={{ padding: '8px 14px', background: '#fff0f0', color: '#ff375f', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Delete</button>
            </div>
            {selectedLog.notes && (
              <div style={{ background: 'var(--bg-input)', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', borderLeft: '3px solid #e5e5ea' }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.8px', marginBottom: '4px' }}>NOTES</p>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{selectedLog.notes}</p>
              </div>
            )}
            <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>Tap any set to edit it.</p>
            {Object.entries(groupedLogSets).map(([name, { category, sets }]) => {
              const isCardio = category === CARDIO_CATEGORY
              const pr = personalRecords.find(p => p.exercise_name === name)
              return (
                <div key={name} style={{ marginBottom: '16px', background: 'var(--bg-input)', borderRadius: '12px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{name}</p>
                  {pr && !isCardio && (
                    <p style={{ fontSize: '11px', color: '#ff9f0a', fontWeight: '600', marginBottom: '10px' }}>🏆 PR: {pr.best_weight} lbs x {pr.best_reps} reps</p>
                  )}
                  {isCardio ? (
                    sets.map(set => (
                      <div key={set.id}>
                        {editingSet === set.id ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '8px', alignItems: 'flex-end' }}>
                            <div>
                              <p style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '4px' }}>DURATION (min)</p>
                              <input type="number" value={editValues.reps} onChange={e => setEditValues({ ...editValues, reps: e.target.value })} style={inputStyle} autoFocus />
                            </div>
                            <div>
                              <p style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '4px' }}>DISTANCE (mi)</p>
                              <input type="number" step="0.1" value={editValues.weight} onChange={e => setEditValues({ ...editValues, weight: e.target.value })} style={inputStyle} />
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => saveEditSet(set)} disabled={saving} style={{ flex: 1, padding: '10px 4px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Save</button>
                              <button onClick={cancelEdit} style={{ flex: 1, padding: '10px 4px', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>x</button>
                            </div>
                          </div>
                        ) : (
                          <div onClick={() => startEditSet(set)} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '8px', alignItems: 'center', cursor: 'pointer', padding: '4px', borderRadius: '6px' }}>
                            <div>
                              <p style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '2px' }}>DURATION</p>
                              <p style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text-primary)' }}>{set.reps} min</p>
                            </div>
                            <div>
                              <p style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '2px' }}>DISTANCE</p>
                              <p style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text-primary)' }}>{set.weight} mi</p>
                            </div>
                            <span style={{ fontSize: '12px', color: '#ff375f', textAlign: 'right' }}>Edit</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 80px', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)' }}>SET</span>
                        <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)' }}>WEIGHT</span>
                        <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-tertiary)' }}>REPS</span>
                        <span></span>
                      </div>
                      {sets.map(set => {
                        const isPR = pr && parseFloat(set.weight) >= parseFloat(pr.best_weight)
                        return (
                          <div key={set.id}>
                            {editingSet === set.id ? (
                              <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 80px', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-tertiary)', textAlign: 'center' }}>{set.set_number}</span>
                                <input type="number" value={editValues.weight} onChange={e => setEditValues({ ...editValues, weight: e.target.value })} style={inputStyle} autoFocus />
                                <input type="number" value={editValues.reps} onChange={e => setEditValues({ ...editValues, reps: e.target.value })} style={inputStyle} />
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button onClick={() => saveEditSet(set)} disabled={saving} style={{ flex: 1, padding: '8px 4px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Save</button>
                                  <button onClick={cancelEdit} style={{ flex: 1, padding: '8px 4px', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>x</button>
                                </div>
                              </div>
                            ) : (
                              <div onClick={() => startEditSet(set)} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 80px', gap: '8px', alignItems: 'center', marginBottom: '6px', cursor: 'pointer', padding: '4px', borderRadius: '6px', background: isPR ? '#fffbf0' : 'transparent' }}>
                                <span style={{ fontSize: '13px', color: 'var(--text-tertiary)', textAlign: 'center' }}>{set.set_number}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>{set.weight} lbs {isPR && '🏆'}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>{set.reps} reps</span>
                                <span style={{ fontSize: '12px', color: '#ff375f', textAlign: 'right' }}>Edit</span>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* SUMMARY TAB */}
      {activeTab === 'summary' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ background: 'linear-gradient(135deg, #1c1c1e, #3a3a3c)', borderRadius: '16px', padding: '24px', color: 'white', boxShadow: '0 1px 3px var(--shadow-md)' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', opacity: 0.6, letterSpacing: '0.8px', marginBottom: '20px' }}>LAST 7 DAYS</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', textAlign: 'center' }}>
              {[
                { value: weekly.workouts, label: 'Workouts' },
                { value: weekly.days, label: 'Active Days' },
                { value: weekly.totalSets, label: 'Total Sets' },
                { value: weekly.totalCalories, label: 'Est. kcal' },
              ].map(stat => (
                <div key={stat.label}>
                  <p style={{ fontSize: isMobile ? '24px' : '30px', fontWeight: '700', lineHeight: 1.1 }}>{stat.value}</p>
                  <p style={{ fontSize: '11px', opacity: 0.65, marginTop: '4px' }}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 3px var(--shadow)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.8px', marginBottom: '6px' }}>CURRENT STREAK</p>
              <p style={{ fontSize: '26px', fontWeight: '700', color: streak > 0 ? '#ff6b35' : '#8e8e93' }}>
                {streak > 0 ? `🔥 ${streak} days` : 'No active streak'}
              </p>
            </div>
            {streak === 0 && <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Log a workout to start one</p>}
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-hint)', padding: '0 4px' }}>
            Calorie estimates use MET values at {userWeightLbs} lbs. Strength is ~5 kcal per set.
          </p>
          <button
            onClick={() => { setWeightInput(String(userWeightLbs)); setShowWeightModal(true) }}
            style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px 16px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500', width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px var(--shadow)' }}
          >
            <span>⚖️ My Weight</span>
            <span style={{ color: '#ff375f', fontWeight: '600' }}>{userWeightLbs} lbs →</span>
          </button>
        </div>
      )}

      {/* PERSONAL RECORDS TAB */}
      {activeTab === 'prs' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.8px' }}>PERSONAL RECORDS</p>
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto' }}>
              {prCategories.map(cat => (
                <button key={cat} onClick={() => setPrFilter(cat)} style={{ padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '500', background: prFilter === cat ? '#ff375f' : 'var(--bg-tertiary)', color: prFilter === cat ? 'white' : 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
          {filteredPRs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: 'var(--bg-card)', borderRadius: '16px', boxShadow: '0 1px 3px var(--shadow)' }}>
              <p style={{ fontSize: '40px', marginBottom: '12px' }}>🏆</p>
              <p style={{ fontSize: '15px', color: 'var(--text-tertiary)' }}>Log workouts to start tracking your personal records.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredPRs.map(pr => (
                <div key={pr.id} style={{ background: 'var(--bg-card)', borderRadius: '14px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px var(--shadow)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{ fontSize: '24px' }}>🏆</span>
                    <div>
                      <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '2px' }}>{pr.exercise_name}</p>
                      <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                        {pr.best_weight} lbs x {pr.best_reps} reps · {new Date(pr.achieved_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#ff375f' }}>{pr.best_weight}</p>
                    <p style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>lbs</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* WEIGHT MODAL */}
      {showWeightModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }} onClick={() => setShowWeightModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '320px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '6px' }}>Your Weight</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '20px' }}>Used to estimate calories burned during cardio.</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
              <input
                type="number"
                value={weightInput}
                onChange={e => setWeightInput(e.target.value)}
                placeholder="185"
                autoFocus
                style={{ ...inputStyle, flex: 1, fontSize: '24px', fontWeight: '700', textAlign: 'center' }}
              />
              <span style={{ fontSize: '16px', color: 'var(--text-tertiary)', fontWeight: '500' }}>lbs</span>
            </div>
            <button
              onClick={() => {
                const val = parseFloat(weightInput)
                if (val > 0) {
                  setUserWeightLbs(val)
                  localStorage.setItem('bf_weight_lbs', String(val))
                }
                setShowWeightModal(false)
              }}
              style={{ width: '100%', padding: '14px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}
            >Save Weight</button>
          </div>
        </div>
      )}

      {/* EXERCISE PICKER MODAL */}
      {showExercisePicker && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setShowExercisePicker(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', padding: '24px', width: '100%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>Add Exercise</h3>
              <button onClick={() => setShowExercisePicker(false)} style={{ background: 'var(--bg-tertiary)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px', color: 'var(--text-secondary)' }}>x</button>
            </div>
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '16px' }}>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)} style={{ padding: '6px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', background: selectedCategory === cat ? '#ff375f' : 'var(--bg-tertiary)', color: selectedCategory === cat ? 'white' : 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {cat}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {filteredExercises.map(ex => {
                const pr = personalRecords.find(p => p.exercise_name === ex.name)
                return (
                  <button key={ex.id} onClick={() => handleExerciseSelect(ex)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: '12px', border: 'none', background: 'var(--bg-input)', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div>
                      <span style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text-primary)' }}>{ex.name}</span>
                      {pr && <p style={{ fontSize: '11px', color: '#ff9f0a', marginTop: '2px' }}>🏆 {pr.best_weight} lbs x {pr.best_reps} reps</p>}
                    </div>
                    <span style={{ fontSize: '20px', color: '#ff375f', fontWeight: '200' }}>+</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* SET COUNT MODAL */}
      {showSetModal && pendingExercise && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }} onClick={() => setShowSetModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '340px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>{pendingExercise.name}</h3>
            {personalRecords.find(p => p.exercise_name === pendingExercise.name) && (
              <p style={{ fontSize: '12px', color: '#ff9f0a', fontWeight: '600', marginBottom: '12px' }}>
                🏆 PR: {personalRecords.find(p => p.exercise_name === pendingExercise.name).best_weight} lbs x {personalRecords.find(p => p.exercise_name === pendingExercise.name).best_reps} reps
              </p>
            )}
            <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '24px' }}>How many sets?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '24px' }}>
              {[2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setNumSets(n)} style={{ width: '52px', height: '52px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '20px', fontWeight: '700', background: numSets === n ? '#ff375f' : 'var(--bg-tertiary)', color: numSets === n ? 'white' : 'var(--text-primary)' }}>
                  {n}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Custom:</p>
              <input type="number" value={numSets} onChange={e => setNumSets(parseInt(e.target.value) || 1)} min="1" max="20" style={{ width: '60px', padding: '8px', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '16px', textAlign: 'center' }} />
            </div>
            <button onClick={confirmAddExercise} style={{ width: '100%', padding: '14px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}>
              Add {numSets} Sets
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const inputStyle = {
  padding: '10px',
  border: '1.5px solid var(--border)',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: '500',
  textAlign: 'center',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  width: '100%',
}

export default Fitness
